import React, { useEffect, useMemo, useState } from "react";
import apiClient, { formatApiError } from "@/api/client";
import { Plus, X, Search, Pin, PinOff, Archive, ArchiveRestore, Trash2, Tag, ListChecks, FileText, Lightbulb, ClipboardList, Users, GraduationCap, User, Star, Bell } from "lucide-react";
import { toast } from "sonner";
import CustomerSelect from "@/components/CustomerSelect";

const CATEGORIES = [
  { key: "all", label: "Tümü", icon: FileText, color: "#4F46E5" },
  { key: "genel", label: "Genel", icon: FileText, color: "#64748B" },
  { key: "fikir", label: "Fikir", icon: Lightbulb, color: "#F59E0B" },
  { key: "todo", label: "Yapılacak", icon: ClipboardList, color: "#10B981" },
  { key: "musteri", label: "Müşteri", icon: Users, color: "#4F46E5" },
  { key: "egitim", label: "Eğitim", icon: GraduationCap, color: "#7C3AED" },
  { key: "kisisel", label: "Kişisel", icon: User, color: "#EC4899" },
];

const COLORS = [
  { key: "yellow", bg: "bg-[#FEF3C7]", border: "border-[#FDE68A]" },
  { key: "blue", bg: "bg-[#DBEAFE]", border: "border-[#BFDBFE]" },
  { key: "green", bg: "bg-[#DCFCE7]", border: "border-[#BBF7D0]" },
  { key: "pink", bg: "bg-[#FCE7F3]", border: "border-[#FBCFE8]" },
  { key: "purple", bg: "bg-[#EDE9FE]", border: "border-[#DDD6FE]" },
  { key: "orange", bg: "bg-[#FFEDD5]", border: "border-[#FED7AA]" },
  { key: "gray", bg: "bg-[#F1F5F9]", border: "border-[#E2E8F0]" },
];

const EMPTY = { title: "", body: "", color: "yellow", pinned: false, tags: [], category: "genel", checklist: [], archived: false, reminder_at: null, customer_id: null };

export default function NotesPage() {
  const [notes, setNotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, pinned: 0, archived: 0, by_category: {}, top_tags: [] });
  const [filter, setFilter] = useState({ category: "all", tag: "", archived: false, q: "" });
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [customers, setCustomers] = useState([]);

  const load = async () => {
    const params = {};
    if (filter.category && filter.category !== "all") params.category = filter.category;
    if (filter.tag) params.tag = filter.tag;
    if (filter.archived) params.archived = true;
    if (filter.q) params.q = filter.q;
    const [{ data: ns }, { data: st }, { data: cs }] = await Promise.all([
      apiClient.get("/me/notes", { params }),
      apiClient.get("/me/notes/stats"),
      apiClient.get("/customers"),
    ]);
    setNotes(ns);
    setStats(st);
    setCustomers(cs);
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const save = async (ev) => {
    ev.preventDefault();
    try {
      const payload = { ...form, tags: form.tags.filter(Boolean), checklist: (form.checklist || []).filter(c => c.text?.trim()), customer_id: form.customer_id || null };
      if (edit) await apiClient.put(`/me/notes/${edit.id}`, payload);
      else await apiClient.post("/me/notes", payload);
      setOpen(false); setEdit(null); setForm(EMPTY); load();
      toast.success(edit ? "Not güncellendi" : "Not eklendi");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const togglePin = async (n) => { await apiClient.put(`/me/notes/${n.id}`, { ...n, pinned: !n.pinned }); load(); };
  const toggleArchive = async (n) => { await apiClient.put(`/me/notes/${n.id}`, { ...n, archived: !n.archived }); load(); toast.success(n.archived ? "Arşivden çıkarıldı" : "Arşivlendi"); };
  const remove = async (n) => { if (!window.confirm("Notu silmek istediğine emin misin?")) return; await apiClient.delete(`/me/notes/${n.id}`); load(); toast.success("Silindi"); };
  const startEdit = (n) => { setEdit(n); setForm({ ...EMPTY, ...n, tags: n.tags || [], checklist: n.checklist || [] }); setOpen(true); };
  const startNew = (cat = "genel") => { setEdit(null); setForm({ ...EMPTY, category: cat }); setOpen(true); };

  const filteredCount = notes.length;
  const grouped = useMemo(() => {
    const pinned = notes.filter(n => n.pinned);
    const others = notes.filter(n => !n.pinned);
    return { pinned, others };
  }, [notes]);

  return (
    <div className="space-y-4" data-testid="notes-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Notlar</h1>
          <p className="text-sm text-[#64748B] mt-1">{stats.total} aktif · {stats.pinned} sabit · {stats.archived} arşivlenmiş</p>
        </div>
        <button onClick={() => startNew(filter.category !== "all" ? filter.category : "genel")} className="ky-btn ky-btn-accent" data-testid="new-note"><Plus size={14}/>Yeni Not</button>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Sidebar */}
        <div className="col-span-12 md:col-span-3 space-y-4">
          <div className="ky-card p-3">
            <div className="text-[11px] uppercase tracking-wider text-[#94A3B8] font-semibold mb-2 px-2">Kategoriler</div>
            <div className="space-y-0.5">
              {CATEGORIES.map(c => {
                const Icon = c.icon;
                const count = c.key === "all" ? stats.total : (stats.by_category?.[c.key] || 0);
                const active = filter.category === c.key && !filter.archived;
                return (
                  <button key={c.key} onClick={() => setFilter({ ...filter, category: c.key, archived: false, tag: "" })} className={`w-full flex items-center justify-between px-2 py-1.5 rounded-md text-sm hover:bg-[#F4F4F5] ${active ? "bg-[#EEF2FF] text-[#4F46E5] font-medium" : "text-[#0F172A]"}`} data-testid={`cat-${c.key}`}>
                    <span className="flex items-center gap-2"><Icon size={13} style={{ color: c.color }}/>{c.label}</span>
                    <span className="text-[10px] font-mono text-[#94A3B8]">{count}</span>
                  </button>
                );
              })}
            </div>
            <hr className="my-2 border-[#ECECEF]"/>
            <button onClick={() => setFilter({ ...filter, archived: !filter.archived, tag: "" })} className={`w-full flex items-center justify-between px-2 py-1.5 rounded-md text-sm hover:bg-[#F4F4F5] ${filter.archived ? "bg-[#FEF3C7] text-[#92400E] font-medium" : "text-[#0F172A]"}`} data-testid="cat-archived">
              <span className="flex items-center gap-2"><Archive size={13}/>Arşiv</span>
              <span className="text-[10px] font-mono text-[#94A3B8]">{stats.archived}</span>
            </button>
          </div>

          {(stats.top_tags || []).length > 0 && (
            <div className="ky-card p-3">
              <div className="text-[11px] uppercase tracking-wider text-[#94A3B8] font-semibold mb-2 px-2 flex items-center gap-1"><Tag size={11}/>Etiketler</div>
              <div className="flex flex-wrap gap-1">
                {(stats.top_tags || []).map(t => (
                  <button key={t.name} onClick={() => setFilter({ ...filter, tag: filter.tag === t.name ? "" : t.name })} className={`px-2 py-0.5 rounded-full text-[11px] border ${filter.tag === t.name ? "bg-[#4F46E5] text-white border-[#4F46E5]" : "bg-[#F4F4F5] border-[#ECECEF]"}`}>
                    #{t.name} <span className="opacity-60">{t.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Main */}
        <div className="col-span-12 md:col-span-9 space-y-3">
          <div className="ky-card p-2 flex items-center gap-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none"/>
              <input value={filter.q} onChange={e => setFilter({ ...filter, q: e.target.value })} placeholder="Notlarımda ara…" className="ky-input pl-9" data-testid="notes-search"/>
            </div>
            {filter.tag && <span className="px-2 py-1 text-xs bg-[#EEF2FF] text-[#4F46E5] rounded-md flex items-center gap-1">#{filter.tag} <button onClick={() => setFilter({ ...filter, tag: "" })}><X size={11}/></button></span>}
          </div>

          {filteredCount === 0 && (
            <div className="ky-card p-12 text-center text-[#94A3B8] space-y-2">
              <FileText size={40} className="mx-auto opacity-30"/>
              <div className="text-sm">{filter.archived ? "Arşivlenmiş not yok." : filter.q ? `"${filter.q}" için sonuç yok.` : "Henüz not yok. İlk notunu ekle!"}</div>
              {!filter.archived && !filter.q && <button onClick={() => startNew()} className="ky-btn ky-btn-accent mt-2"><Plus size={13}/>İlk Notu Oluştur</button>}
            </div>
          )}

          {grouped.pinned.length > 0 && (
            <>
              <div className="text-[11px] uppercase tracking-wider text-[#94A3B8] font-semibold flex items-center gap-1"><Pin size={11}/>Sabitlenmiş</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {grouped.pinned.map(n => <NoteCard key={n.id} note={n} onEdit={startEdit} onPin={togglePin} onArchive={toggleArchive} onDelete={remove}/>)}
              </div>
            </>
          )}
          {grouped.others.length > 0 && (
            <>
              {grouped.pinned.length > 0 && <div className="text-[11px] uppercase tracking-wider text-[#94A3B8] font-semibold">Diğerleri</div>}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {grouped.others.map(n => <NoteCard key={n.id} note={n} onEdit={startEdit} onPin={togglePin} onArchive={toggleArchive} onDelete={remove}/>)}
              </div>
            </>
          )}
        </div>
      </div>

      {open && <NoteEditor form={form} setForm={setForm} edit={edit} customers={customers} onClose={() => { setOpen(false); setEdit(null); setForm(EMPTY); }} onSave={save}/>}
    </div>
  );
}

function NoteCard({ note, onEdit, onPin, onArchive, onDelete }) {
  const color = COLORS.find(c => c.key === note.color) || COLORS[0];
  const cat = CATEGORIES.find(c => c.key === note.category) || CATEGORIES[1];
  const Icon = cat.icon;
  const checkDone = (note.checklist || []).filter(c => c.done).length;
  const checkTotal = (note.checklist || []).length;
  return (
    <div className={`group relative rounded-xl border ${color.bg} ${color.border} p-3 cursor-pointer hover:shadow-md transition-shadow`} onClick={() => onEdit(note)} data-testid={`note-${note.id}`}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#475569] font-semibold">
          <Icon size={11} style={{ color: cat.color }}/>{cat.label}
          {note.customer_id && <span className="ml-1 px-1.5 py-0.5 rounded bg-white/60 text-[#4F46E5] normal-case font-medium flex items-center gap-1"><Users size={9}/>Müşteri</span>}
        </div>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={e => { e.stopPropagation(); onPin(note); }} title={note.pinned ? "Sabitlemeyi kaldır" : "Sabitle"} className="p-1 rounded hover:bg-white/60">{note.pinned ? <PinOff size={12}/> : <Pin size={12}/>}</button>
          <button onClick={e => { e.stopPropagation(); onArchive(note); }} title={note.archived ? "Arşivden çıkar" : "Arşivle"} className="p-1 rounded hover:bg-white/60">{note.archived ? <ArchiveRestore size={12}/> : <Archive size={12}/>}</button>
          <button onClick={e => { e.stopPropagation(); onDelete(note); }} title="Sil" className="p-1 rounded hover:bg-white/60 text-[#EF4444]"><Trash2 size={12}/></button>
        </div>
      </div>
      <div className="font-semibold text-sm text-[#0F172A] line-clamp-2">{note.title}</div>
      {note.body && <div className="text-xs text-[#475569] mt-1 line-clamp-4 whitespace-pre-wrap">{note.body}</div>}
      {checkTotal > 0 && (
        <div className="mt-2 flex items-center gap-1 text-[11px] text-[#475569]"><ListChecks size={11}/>{checkDone}/{checkTotal} tamamlandı
          <div className="flex-1 h-1 bg-white/50 rounded-full overflow-hidden ml-1"><div className="h-full bg-[#10B981]" style={{ width: `${(checkDone/checkTotal)*100}%` }}/></div>
        </div>
      )}
      {(note.tags || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {(note.tags || []).map(t => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-white/60 text-[#475569]">#{t}</span>)}
        </div>
      )}
      {note.reminder_at && <div className="mt-2 text-[10px] text-[#92400E] flex items-center gap-1"><Bell size={9}/>{new Date(note.reminder_at).toLocaleString("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>}
    </div>
  );
}

function NoteEditor({ form, setForm, edit, customers, onClose, onSave }) {
  const [newTag, setNewTag] = useState("");
  const [newCheck, setNewCheck] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/40"/>
      <form onSubmit={onSave} onClick={e => e.stopPropagation()} className="w-full sm:w-[560px] bg-white flex flex-col ky-slide-in" data-testid="note-editor">
        <div className="px-5 py-4 border-b border-[#ECECEF] flex items-center justify-between">
          <h3 className="font-semibold">{edit ? "Notu Düzenle" : "Yeni Not"}</h3>
          <button type="button" onClick={onClose} className="ky-btn ky-btn-ghost ky-btn-icon"><X size={14}/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Başlık" className="ky-input text-base font-semibold" data-testid="note-title" autoFocus/>
          <textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} placeholder="İçerik…" rows={8} className="ky-textarea"/>

          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-1.5 flex items-center gap-1"><Users size={11}/>Müşteriye bağla (opsiyonel)</label>
            <CustomerSelect customers={customers || []} value={form.customer_id || null} onChange={(v) => setForm({ ...form, customer_id: v || null })} placeholder="— Müşteri seçilmedi —" testid="note-customer"/>
            {form.customer_id && <div className="text-[11px] text-[#64748B] mt-1">Bu not müşterinin profilindeki "Notlar" sekmesinde de görünecek.</div>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#64748B] mb-1.5">Kategori</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="ky-select">
                {CATEGORIES.filter(c => c.key !== "all").map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#64748B] mb-1.5">Renk</label>
              <div className="flex gap-1.5">{COLORS.map(c => <button key={c.key} type="button" onClick={() => setForm({ ...form, color: c.key })} className={`w-7 h-7 rounded-md border-2 ${c.bg} ${form.color === c.key ? "border-[#0F172A]" : "border-transparent"}`}/>)}</div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-1.5 flex items-center gap-1"><Tag size={11}/>Etiketler</label>
            <div className="flex flex-wrap gap-1 mb-1.5">
              {(form.tags || []).map(t => (
                <span key={t} className="px-2 py-1 text-xs rounded-full bg-[#EEF2FF] text-[#4F46E5] flex items-center gap-1">#{t}<button type="button" onClick={() => setForm({ ...form, tags: form.tags.filter(x => x !== t) })}><X size={10}/></button></span>
              ))}
            </div>
            <input value={newTag} onChange={e => setNewTag(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && newTag.trim()) { e.preventDefault(); setForm({ ...form, tags: [...(form.tags || []), newTag.trim()] }); setNewTag(""); } }} placeholder="Etiket ekle (Enter)" className="ky-input"/>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-1.5 flex items-center gap-1"><ListChecks size={11}/>Yapılacaklar</label>
            <div className="space-y-1 mb-1.5">
              {(form.checklist || []).map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="checkbox" checked={!!c.done} onChange={e => { const nc = [...form.checklist]; nc[i] = { ...nc[i], done: e.target.checked }; setForm({ ...form, checklist: nc }); }}/>
                  <input value={c.text} onChange={e => { const nc = [...form.checklist]; nc[i] = { ...nc[i], text: e.target.value }; setForm({ ...form, checklist: nc }); }} className="ky-input flex-1" placeholder="Görev"/>
                  <button type="button" onClick={() => setForm({ ...form, checklist: form.checklist.filter((_, j) => j !== i) })}><X size={13}/></button>
                </div>
              ))}
            </div>
            <input value={newCheck} onChange={e => setNewCheck(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && newCheck.trim()) { e.preventDefault(); setForm({ ...form, checklist: [...(form.checklist || []), { text: newCheck.trim(), done: false }] }); setNewCheck(""); } }} placeholder="Yeni madde ekle (Enter)" className="ky-input"/>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={!!form.pinned} onChange={e => setForm({ ...form, pinned: e.target.checked })}/><Pin size={12}/>Sabitle</label>
            <div className="flex items-center gap-1 text-sm"><Bell size={12}/><input type="datetime-local" value={form.reminder_at ? form.reminder_at.slice(0, 16) : ""} onChange={e => setForm({ ...form, reminder_at: e.target.value ? new Date(e.target.value).toISOString() : null })} className="ky-input"/></div>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[#ECECEF] flex gap-2">
          <button type="button" onClick={onClose} className="ky-btn ky-btn-outline flex-1">İptal</button>
          <button type="submit" className="ky-btn ky-btn-accent flex-1" data-testid="save-note">Kaydet</button>
        </div>
      </form>
    </div>
  );
}
